import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Check, X, Crown, Star, Sparkles, Send, Gift, Zap, Camera, Video, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import WaitlistDialog from '../components/WaitlistDialog';
import CheckoutPausedBanner from '../components/CheckoutPausedBanner';

const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const { t } = useLanguage();

  const oneTimePricing = [
    {
      sites: 1,
      price: 29,
      popular: false
    },
    {
      sites: 3,
      price: 49,
      popular: true,
      savings: 'Save $38'
    },
    {
      sites: 5,
      price: 79,
      popular: false,
      savings: 'Save $66'
    }
  ];

  const subscriptionPlans = [
    {
      id: 'foundation',
      name: 'Foundation',
      icon: Star,
      tagline: 'Begin Your Sacred Journey',
      monthlyPrice: 19,
      annualPrice: 192,
      description: 'A solid start for your spiritual connection',
      features: [
        'Choose any 1 holy site monthly',
        'Submit new prayer each month',
        'High-quality photo confirmation',
        'Email delivery confirmation',
        'GPS coordinates of placement',
        'Priority email support'
      ],
      limitations: [
        'One site per delivery',
        'Monthly delivery only'
      ],
      popular: false,
      color: 'blue'
    },
    {
      id: 'devotion',
      name: 'Devotion',
      icon: Crown,
      tagline: 'Deepen Your Faith',
      monthlyPrice: 39,
      annualPrice: 396,
      description: 'Committed prayer for faithful hearts',
      features: [
        'Fixed holy site of your choice',
        'Submit 2 prayers per month',
        'Bi-weekly delivery schedule',
        'All photo confirmation benefits',
        'GPS coordinates included',
        'Personal delivery notes from team',
        'Priority support + phone access'
      ],
      limitations: [
        'Same site for all deliveries'
      ],
      popular: false,
      color: 'purple'
    },
    {
      id: 'sanctuary',
      name: 'Sanctuary',
      icon: Sparkles,
      tagline: 'Complete Spiritual Covering',
      monthlyPrice: 59,
      annualPrice: 588,
      description: 'Full ministry for you and your loved ones',
      features: [
        'Choose ANY site for each delivery',
        'Submit new prayer anytime',
        'Weekly delivery schedule (4x per month)',
        'All premium benefits included',
        'Dedicated team member assigned',
        'Video messages from holy sites',
        'Priority expedited delivery',
        '24/7 support access',
        'Annual Holy Land blessing package'
      ],
      limitations: [],
      popular: true,
      color: 'amber'
    }
  ];

  const getPrice = (plan) => {
    return billingPeriod === 'monthly' ? plan.monthlyPrice : Math.round(plan.annualPrice / 12);
  };

  const getTotalPrice = (plan) => {
    return billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  };

  const getSavings = (plan) => {
    const monthlyCost = plan.monthlyPrice * 12;
    const annualCost = plan.annualPrice;
    return monthlyCost - annualCost;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-24 pb-16">
      <SEO title="Pricing — Send Your Prayer to Jerusalem from $29" description="Send your prayer request to Jerusalem's holy sites starting at just $29. Choose 1, 3, or 5 sacred sites — the Church of the Holy Sepulchre, Western Wall, Via Dolorosa and more. Photo proof and 30-day money-back guarantee included." path="/pricing" />
      {/* Hero Section */}
      <div className="container mx-auto px-4 mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full shadow-xl">
              <Gift className="h-12 w-12 text-white" />
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {t('pricing.title')}
          </h1>
          <p className="text-base md:text-xl text-gray-600 mb-4">
            {t('pricing.subtitle')}
          </p>
          <CheckoutPausedBanner className="mt-8" onJoin={() => setWaitlistOpen(true)} />
        </div>
      </div>
      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} source="pricing" />

      {/* Service Boundaries — legal clarity per FTC/EU consumer-protection guidance.
          Sets accurate expectations for a physical delivery service. */}
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto -mt-2 mb-12">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 md:p-6 text-sm md:text-[15px] leading-relaxed text-amber-900" data-testid="service-boundaries-note">
            <p className="mb-0">
              <strong>What you are purchasing:</strong> a physical delivery service. We print your written prayer on our Weekly Sacred Scroll, personally carry it to the holy sites you select in Jerusalem, and email you a dated photograph as proof of delivery. We do not sell blessings, promise spiritual outcomes, or pray on your behalf.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Emergency Rush callout — above all pricing. For visitors in crisis. */}
        <div className="max-w-6xl mx-auto mb-10">
          <Link
            to="/urgent"
            data-testid="pricing-urgent-callout"
            className="group block relative overflow-hidden rounded-2xl border-2 border-red-200 bg-gradient-to-r from-red-50 via-amber-50 to-red-50 hover:from-red-100 hover:via-amber-100 hover:to-red-100 transition-all shadow-md hover:shadow-xl"
          >
            <div className="flex flex-col md:flex-row items-stretch">
              <div className="flex items-center gap-3 bg-red-600 text-white px-5 py-4 md:px-6 md:py-6 md:w-56 flex-shrink-0">
                <Zap className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-[10px] md:text-xs font-bold uppercase tracking-wider opacity-90">Emergency Rush</p>
                  <p className="text-xl md:text-2xl font-serif font-bold leading-tight">$150</p>
                </div>
              </div>
              <div className="flex-1 flex items-center justify-between px-5 py-4 md:px-6 md:py-5 gap-4">
                <div>
                  <p className="font-semibold text-gray-900 text-sm md:text-base leading-tight">
                    Need your prayer in Jerusalem by tomorrow?
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 mt-1 leading-relaxed">
                    Next-business-day hand delivery to one holy site, with photo & short video clip emailed to you.
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 text-red-700 font-semibold text-sm whitespace-nowrap group-hover:gap-2 transition-all">
                  Learn more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* One-Time Prayer Pricing */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">{t('pricing.oneTime')}</h2>
            <p className="text-base md:text-xl text-gray-600">{t('pricing.oneTimeDesc')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {oneTimePricing.map((option, index) => (
              <Card key={index} className={`relative hover:shadow-2xl transition-all ${
                option.popular ? 'border-2 border-amber-500 lg:transform lg:scale-105' : 'border-2 border-gray-200'
              }`}>
                {option.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-amber-500 text-white text-sm font-bold px-4 py-1 rounded-full whitespace-nowrap">
                      {t('pricing.popular')}
                    </span>
                  </div>
                )}
                <CardContent className="p-8 text-center">
                  <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">{option.sites}</div>
                  <div className="text-gray-600 mb-6">{option.sites === 1 ? t('pricing.holySite') : t('pricing.holySites')}</div>
                  
                  <div className="mb-6">
                    <span className="text-3xl md:text-4xl font-bold text-amber-600">${option.price}</span>
                  </div>

                  {option.savings && (
                    <div className="bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium mb-6">
                      {option.savings}
                    </div>
                  )}

                  <ul className="space-y-3 mb-8 text-left">
                    <li className="flex items-start">
                      <Check className="text-green-500 mr-2 mt-1 flex-shrink-0" size={18} />
                      <span className="text-gray-700">High-quality photos</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="text-green-500 mr-2 mt-1 flex-shrink-0" size={18} />
                      <span className="text-gray-700">GPS-tagged delivery photo</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="text-green-500 mr-2 mt-1 flex-shrink-0" size={18} />
                      <span className="text-gray-700">3-7 day delivery</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="text-green-500 mr-2 mt-1 flex-shrink-0" size={18} />
                      <span className="text-gray-700">Email confirmation</span>
                    </li>
                  </ul>

                  <Link to={`/prayer-request?plan=${option.sites}-sites`}>
                    <Button className={`w-full ${
                      option.popular 
                        ? 'bg-amber-600 hover:bg-amber-700' 
                        : 'bg-gray-800 hover:bg-gray-900'
                    } text-white py-6 text-lg`}>
                      <Send className="mr-2" size={18} />
                      {t('pricing.selectPlan')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}

            {/* 4th card — Emergency Rush */}
            <Card className="relative hover:shadow-2xl transition-all border-2 border-red-500 bg-gradient-to-b from-red-50/50 to-white" data-testid="pricing-card-urgent">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider flex items-center gap-1">
                  <Zap className="w-3 h-3" aria-hidden="true" /> Rush
                </span>
              </div>
              <CardContent className="p-8 text-center">
                <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">1</div>
                <div className="text-gray-600 mb-6">Holy Site · Next business day</div>

                <div className="mb-6">
                  <span className="text-3xl md:text-4xl font-bold text-red-600">$150</span>
                </div>

                <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm font-medium mb-6">
                  Emergency delivery
                </div>

                <ul className="space-y-3 mb-8 text-left">
                  <li className="flex items-start">
                    <Clock className="text-red-500 mr-2 mt-1 flex-shrink-0" size={18} />
                    <span className="text-gray-700">Next business day in Jerusalem</span>
                  </li>
                  <li className="flex items-start">
                    <Camera className="text-red-500 mr-2 mt-1 flex-shrink-0" size={18} />
                    <span className="text-gray-700">Dated photo at the site</span>
                  </li>
                  <li className="flex items-start">
                    <Video className="text-red-500 mr-2 mt-1 flex-shrink-0" size={18} />
                    <span className="text-gray-700">Short video clip (15–30s)</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="text-red-500 mr-2 mt-1 flex-shrink-0" size={18} />
                    <span className="text-gray-700">Priority courier dispatch</span>
                  </li>
                </ul>

                <Link to="/urgent">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg">
                    <Zap className="mr-2" size={18} />
                    Rush my prayer
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Subscription Plans */}
        <div className="max-w-7xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">{t('pricing.subscription')}</h2>
            <p className="text-base md:text-xl text-gray-600 mb-8">{t('pricing.subscriptionDesc')}</p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-gray-900 shadow-md'
                    : 'text-gray-600'
                }`}
              >
                {t('pricing.monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-3 rounded-full font-medium transition-all ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-gray-900 shadow-md'
                    : 'text-gray-600'
                }`}
              >
                {t('pricing.annual')}
                <span className="ml-2 text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                  {t('pricing.savePercent')}
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {subscriptionPlans.map((plan) => {
              const Icon = plan.icon;
              return (
                <Card key={plan.id} className={`relative hover:shadow-2xl transition-all ${
                  plan.popular ? 'border-2 border-amber-500 transform scale-105' : 'border-2 border-gray-200'
                }`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg">
                        ⭐ {t('pricing.popular')}
                      </span>
                    </div>
                  )}
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <div className={`inline-flex p-4 rounded-full bg-gradient-to-br from-${plan.color}-100 to-${plan.color}-200 mb-4`}>
                        <Icon className={`h-8 w-8 text-${plan.color}-600`} />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <p className="text-gray-600 mb-4">{plan.tagline}</p>
                      
                      <div className="mb-4">
                        <span className="text-4xl md:text-5xl font-bold text-gray-900">${getPrice(plan)}</span>
                        <span className="text-gray-600">/month</span>
                      </div>

                      {billingPeriod === 'annual' && (
                        <div className="bg-green-50 text-green-700 px-3 py-2 rounded-lg text-sm font-medium mb-2">
                          Save ${getSavings(plan)}/year
                        </div>
                      )}

                      <div className="text-sm text-gray-500">
                        {billingPeriod === 'annual' 
                          ? `$${getTotalPrice(plan)} billed annually`
                          : 'Billed monthly'
                        }
                      </div>
                    </div>

                    <p className="text-center text-gray-700 mb-6 italic">{plan.description}</p>

                    <div className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <Check className="text-green-500 mr-3 mt-1 flex-shrink-0" size={18} />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                      {plan.limitations.map((limitation, idx) => (
                        <div key={idx} className="flex items-start">
                          <X className="text-gray-400 mr-3 mt-1 flex-shrink-0" size={18} />
                          <span className="text-gray-500">{limitation}</span>
                        </div>
                      ))}
                    </div>

                    <Link to={`/prayer-request?subscription=${plan.id}&billing=${billingPeriod}`}>
                      <Button className={`w-full py-6 text-lg ${
                        plan.popular
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                          : 'bg-gray-800 hover:bg-gray-900'
                      } text-white`}>
                        Choose {plan.name}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Gift Cards */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-12 text-center">
              <Gift className="h-16 w-16 text-purple-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Gift a Prayer Experience
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Give the meaningful gift of a Holy Land prayer connection. Perfect for birthdays, anniversaries, 
                baptisms, confirmations, or any special spiritual milestone.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                {[50, 100, 200, 'Custom'].map((amount, idx) => (
                  <div key={idx} className="bg-white border-2 border-purple-300 rounded-lg p-4 hover:border-purple-500 transition-colors cursor-pointer">
                    <div className="text-2xl font-bold text-purple-600">
                      {typeof amount === 'number' ? `$${amount}` : amount}
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg">
                  <Gift className="mr-2" size={20} />
                  Purchase Gift Card
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Pricing Questions</h2>
          <div className="space-y-6">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">What's included in the price?</h3>
                <p className="text-gray-700">
                  All prices include: professional prayer printing, personal delivery to holy sites by our Jerusalem team,
                  high-quality photographs, GPS coordinates, and email confirmation. There are no hidden fees.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Can I cancel my subscription anytime?</h3>
                <p className="text-gray-700">
                  Yes! Cancel anytime through your account dashboard. Cancellation takes effect at the end of your current billing period, 
                  so you receive all services you've paid for. No cancellation fees ever.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Do you offer refunds?</h3>
                <p className="text-gray-700">
                  Yes. We offer a full 30-day money-back guarantee from the date of purchase, no questions asked. If you're not
                  happy for any reason, email us and we'll refund you in full. See our <a href="/refund" className="text-amber-700 underline">Refund Policy</a> for details.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Why do subscriptions cost less?</h3>
                <p className="text-gray-700">
                  Subscriptions allow us to plan deliveries more efficiently and build ongoing relationships with our messengers in Jerusalem. 
                  We pass these operational savings to you while providing enhanced benefits like dedicated team members and priority support.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
